import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const AddProduct = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: "45px",
            height: "45px",
            borderRadius: "8px",
            borderColor: state.isFocused ? "#2e4a3d" : "#dee2e6",
            boxShadow: state.isFocused ? "0 0 0 0.1rem rgba(46, 74, 61, 0.25)" : "none",
            "&:hover": { borderColor: "#2e4a3d" },
        }),
        valueContainer: (provided) => ({ ...provided, height: "45px", padding: "0 12px" }),
        input: (provided) => ({ ...provided, margin: "0px" }),
        indicatorSeparator: () => ({ display: "none" }),
        indicatorsContainer: (provided) => ({ ...provided, height: "45px" }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    // Form States
    const [formData, setFormData] = useState({
        name: "",
        code: "",
        unit: "kg", // Default unit based on your PHP
    });

    const [errors, setErrors] = useState({});

    // Unit Options based on your PHP allowed_units
    const unitOptions = [
        { value: "kg", label: "Kilogram (KG)" },
        { value: "quintal", label: "Quintal" },
        { value: "ton", label: "Ton" },
    ];

    const handleNameChange = (e) => {
        const nameVal = e.target.value;
        // Auto-generate code logic like your PHP: slugify and take first 5 chars
        const autoCode = nameVal
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "")
            .substring(0, 5);

        setFormData({
            ...formData,
            name: nameVal,
            code: autoCode,
        });

        if (errors.name) setErrors({ ...errors, name: "" });
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "POST",
                url: "/product/create.php",
                data: formData,
            });

            if (res?.success) {
                Swal.fire("Success!", "Product created successfully", "success");
                navigate("/products"); // Unnoda list page path
                dispatch(loadingFalse());
            } else {
                if (res?.errors) {
                    setErrors(res.errors);
                } else {
                    toast.error(res?.message || "Failed to create product");
                }
                dispatch(loadingFalse());
            }
        } catch (err) {
            Swal.fire("Error", "Server error occurred", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <div className="page-container p-1">
            <div className="page-header mb-4">
                <h6 className="text-muted">Inventory &gt; Products</h6>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold">Create New Product</h3>
                        <p className="text-muted">Add new items to the cotton mill inventory.</p>
                    </div>
                    <Button color="secondary" outline onClick={() => navigate(-1)}>
                        <i className="bx bx-arrow-back"></i> Back
                    </Button>
                </div>
            </div>

            <Row className="justify-content-center">
                <Col lg={12}>
                    <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                        <CardBody className="p-4">
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label className="fw-bold">Product Name</Label>
                                            <Input
                                                type="text"
                                                name="name"
                                                placeholder="e.g. Suvin Cotton"
                                                className={`form-control-lg ${errors.name ? "is-invalid" : ""}`}
                                                style={{ borderRadius: "8px", height: "45px" }}
                                                value={formData.name}
                                                onChange={handleNameChange}
                                                required
                                            />
                                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="fw-bold">Product Code</Label>
                                            <InputGroup>
                                                <InputGroupText className="bg-light">
                                                    <i className="bx bx-barcode"></i>
                                                </InputGroupText>
                                                <Input
                                                    type="text"
                                                    name="code"
                                                    placeholder="Auto-generated"
                                                    className={errors.code ? "is-invalid" : ""}
                                                    style={{ height: "45px" }}
                                                    value={formData.code}
                                                    onChange={handleChange}
                                                />
                                            </InputGroup>
                                            <small className="text-muted">Unique identifier for the product.</small>
                                            {errors.code && <div className="text-danger small mt-1">{errors.code}</div>}
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="fw-bold">Default Unit</Label>
                                            <Select
                                                options={unitOptions}
                                                styles={customSelectStyles}
                                                defaultValue={unitOptions[0]}
                                                onChange={(opt) => setFormData({ ...formData, unit: opt.value })}
                                            />
                                            {errors.unit && <div className="text-danger small mt-1">{errors.unit}</div>}
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <div className="mt-4 pt-3 border-top d-flex justify-content-end gap-2">
                                    <Button
                                        type="button"
                                        color="light"
                                        className="px-4"
                                        onClick={() => navigate(-1)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="add-btn px-5">
                                        <i className="bx bx-plus me-1"></i> Save Product
                                    </Button>
                                </div>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>

            </Row>
        </div>
    );
};

export default AddProduct;